import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { UomSelect } from "@/components/UomSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";
import { fmtNum } from "@/lib/format";
import { WBSCostDialog, inr0 } from "@/pages/project/wbs/WBSCostLines";

const EMPTY = { code: "", name: "", is_group: false, sort_order: 0, uom: "", quantity: "", block_no: "", is_critical: false, remarks: "", parent_id: "none", weight: "" };

export function WBSItemsTable({ projectId, category, canEdit, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogItem, setDialogItem] = useState(null);
  const [costItem, setCostItem] = useState(null);
  const [costTotals, setCostTotals] = useState({});
  const loadCosts = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/wbs-costs`);
      setCostTotals(data.item_totals || {});
    } catch { /* budget column is optional */ }
  }, [projectId]);
  useEffect(() => { loadCosts(); }, [loadCosts]);
  const budgetOf = (it) => (it.is_group
    ? items.filter((c) => c.parent_id === it.id).reduce((s, c) => s + budgetOf(c), 0)
    : Number(costTotals[it.id] || 0));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/wbs`, { params: { category, include_groups: true } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId, category]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/wbs/${id}`);
      toast.success("Item deleted");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const groups = items.filter((i) => i.is_group);

  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`wbs-items-table-${category}`}>
      <div className="flex items-center justify-between border-b border-border p-4">
        <h3 className="text-sm font-semibold text-foreground">{category} items</h3>
        {canEdit && (
          <Button size="sm" onClick={() => setDialogItem(EMPTY)} data-testid={`add-wbs-item-button-${category}`}><Plus className="h-3.5 w-3.5" /> Add item</Button>
        )}
      </div>
      {!loading && items.length === 0 ? (
        <EmptyState title="No items yet" description="Add the first line item for this category." testId={`wbs-empty-${category}`} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>UoM</TableHead><TableHead>Qty</TableHead><TableHead>Critical</TableHead><TableHead className="text-right">Budget</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`wbs-item-row-${i.id}`}>
                <TableCell className="font-mono text-xs">{i.code}</TableCell>
                <TableCell className={i.is_group ? "font-semibold text-foreground" : "text-foreground"}>{i.name}</TableCell>
                <TableCell>{i.is_group ? <Badge variant="secondary">Group</Badge> : <Badge variant="outline">Item</Badge>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{i.uom || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{fmtNum(i.quantity)}</TableCell>
                <TableCell>{i.is_critical && <Badge className="bg-rose-500/15 text-rose-400 border-rose-500/30">Critical</Badge>}</TableCell>
                <TableCell className="text-right font-mono text-xs">{budgetOf(i) ? inr0(budgetOf(i)) : "—"}</TableCell>
                <TableCell className="text-right">
                  {!i.is_group && (
                    <Button size="sm" variant="outline" className="mr-1" onClick={() => setCostItem(i)} data-testid={`wbs-cost-btn-${i.id}`}>₹ Budget</Button>
                  )}
                  {canEdit && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialogItem({ ...i, parent_id: i.parent_id || "none", weight: "" })} data-testid={`edit-wbs-item-btn-${i.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`delete-wbs-item-btn-${i.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {i.code}?</AlertDialogTitle><AlertDialogDescription>This removes the item and its progress history.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(i.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {costItem && (
        <WBSCostDialog projectId={projectId} item={costItem} canEdit={canEdit} onClose={() => setCostItem(null)} onSaved={loadCosts} />
      )}
      {dialogItem && (
        <WBSItemDialog
          projectId={projectId}
          category={category}
          groups={groups}
          initial={dialogItem}
          onClose={() => setDialogItem(null)}
          onSaved={() => { load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function WBSItemDialog({ projectId, category, groups, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const isEdit = Boolean(initial.id);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      const parentId = form.parent_id === "none" ? null : form.parent_id;
      if (isEdit) {
        await api.patch(`/projects/${projectId}/wbs/${form.id}`, {
          code: form.code, name: form.name, is_group: form.is_group, sort_order: Number(form.sort_order) || 0,
          uom: form.uom || null, quantity: form.quantity === "" ? null : Number(form.quantity),
          block_no: form.block_no || null, is_critical: form.is_critical, remarks: form.remarks || null, parent_id: parentId,
        });
      } else {
        await api.post(`/projects/${projectId}/wbs`, {
          code: form.code, name: form.name, category, is_group: form.is_group, sort_order: Number(form.sort_order) || 0,
          uom: form.uom || null, quantity: form.quantity === "" ? null : Number(form.quantity),
          block_no: form.block_no || null, is_critical: form.is_critical, remarks: form.remarks || null, parent_id: parentId,
          weight: form.weight === "" ? null : Number(form.weight),
        });
      }
      toast.success(isEdit ? "Item updated" : "Item created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="wbs-item-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Edit ${form.code}` : `New ${category} item`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input placeholder="Automatic if blank" data-testid="wbs-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
            <div><Label>Sort order</Label><Input type="number" data-testid="wbs-sort-order-input" value={form.sort_order} onChange={set("sort_order")} className="mt-1.5" /></div>
          </div>
          <div><Label>Name</Label><Input data-testid="wbs-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>UoM</Label><UomSelect value={form.uom} onChange={(v) => setForm({ ...form, uom: v })} className="mt-1.5" testId="wbs-uom-input" /></div>
            <div><Label>Quantity (scope)</Label><Input type="number" data-testid="wbs-quantity-input" value={form.quantity} onChange={set("quantity")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Block no.</Label><Input data-testid="wbs-block-input" value={form.block_no} onChange={set("block_no")} className="mt-1.5" /></div>
            <div>
              <Label>Parent group</Label>
              <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                <SelectTrigger data-testid="wbs-parent-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent</SelectItem>
                  {groups.filter((g) => g.id !== form.id).map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!isEdit && (
            <div><Label>Initial weight (0–1, optional)</Label><Input type="number" step="0.01" data-testid="wbs-weight-input" value={form.weight} onChange={set("weight")} className="mt-1.5" /></div>
          )}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><Switch checked={form.is_group} onCheckedChange={(v) => setForm({ ...form, is_group: v })} data-testid="wbs-is-group-switch" /><Label>Is a group header</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_critical} onCheckedChange={(v) => setForm({ ...form, is_critical: v })} data-testid="wbs-is-critical-switch" /><Label>Critical path</Label></div>
          </div>
          <div><Label>Remarks</Label><Textarea data-testid="wbs-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button disabled={!form.name || (isEdit && !form.code)} onClick={submit} data-testid="wbs-item-submit-button">{isEdit ? "Save changes" : "Create item"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
